import { Popover, Portal } from '@ark-ui/react';
import { useState } from 'react';

import type { PrimaryNavigationItem, AncillaryNavigation } from 'src/types/components';

import ArrowDownIcon from 'assets/icons/arrow_down.svg';

import TextCTA from 'components/BaseHelpers/TextCTA';

import Hamburger from '../Hamburger/Hamburger';

import styles from './MobilePrimaryNav.module.scss';

export type MobilePrimaryNavProps = {
  classNames?: string;
  primaryNavigationItems: PrimaryNavigationItem[];
  ancillaryNavigationItems: AncillaryNavigation;
};

type MainNavPanelProps = MobilePrimaryNavProps & {
  setPrimarySubNav: (nav: PrimaryNavigationItem | null) => void;
  closeMenu: () => void;
};

const MainNavPanel = ({
  primaryNavigationItems,
  ancillaryNavigationItems,
  setPrimarySubNav,
  closeMenu,
}: MainNavPanelProps) => {
  return (
    <>
      <ul className={styles.subNavHeadings}>
        {primaryNavigationItems.map((item, index) => {
          return (
            <li key={index}>
              {item.itemType === 'menu' && (
                <TextCTA
                  tag="button"
                  label={item.label}
                  size="large"
                  iconVariant="yellow"
                  className={styles.subNavHeadingCTA}
                  onClick={() => setPrimarySubNav(item)}
                />
              )}
              {item.itemType === 'page' && (
                <TextCTA
                  onClick={closeMenu}
                  tag="a"
                  size="large"
                  iconVariant="yellow"
                  className={styles.subNavHeadingCTA}
                  {...item.page}
                />
              )}
            </li>
          );
        })}
      </ul>
      <ul className={styles.mobileAncillary}>
        {ancillaryNavigationItems.links.map((item, index) => {
          return (
            <li key={index}>
              <TextCTA onClick={closeMenu} tag="a" {...item} iconVariant="yellow">
                {item.label}
              </TextCTA>
            </li>
          );
        })}
      </ul>
    </>
  );
};

type SubNavPanelProps = {
  nav: PrimaryNavigationItem['subMenu'];
  setPrimarySubNav: (nav: PrimaryNavigationItem | null) => void;
  closeMenu: () => void;
};
const SubNavPanel = ({ nav, setPrimarySubNav, closeMenu }: SubNavPanelProps) => {
  if (!nav) {
    return null;
  }
  return (
    <div className={styles.subNav}>
      <div className={styles.subNavHeading}>
        <button onClick={() => setPrimarySubNav(null)} className={styles.backButton}>
          <ArrowDownIcon aria-hidden className={styles.backIcon} />
          {nav.backButtonLabel}
        </button>
        <div className={styles.primaryLinkWrapper}>
          <TextCTA
            tag="a"
            size="large"
            iconVariant="yellow"
            className={styles.subNavHeadingCTA}
            {...nav.primaryLink}
            onClick={closeMenu}
          />
        </div>
      </div>
      <ul className={styles.subNavSectionList}>
        {nav.navigationSections.map((section, index) => {
          const { groupLabel, links } = section;
          return (
            <li key={index} className={styles.subNavSectionItem}>
              {groupLabel && <p className={styles.groupLabel}>{groupLabel}</p>}
              <ul className={styles.subNavSectionLinkList}>
                {links.map((link, index) => {
                  return (
                    <li key={index}>
                      <TextCTA
                        onClick={closeMenu}
                        iconVariant="yellow"
                        className={styles.subNavSectionLinkCTA}
                        tag="a"
                        {...link}
                      />
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const MobilePrimaryNav = ({
  ancillaryNavigationItems,
  primaryNavigationItems,
}: MobilePrimaryNavProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [primarySubNav, setPrimarySubNav] = useState<null | PrimaryNavigationItem>(null);

  const closeMenu = () => {
    setOpen(false);
  };

  return (
    <nav className={styles.nav}>
      <Popover.Root
        open={open}
        onOpenChange={({ open }) => {
          setOpen(open);
          setPrimarySubNav(null);
        }}
        closeOnInteractOutside={false}
        modal
      >
        <Popover.Trigger>
          <Hamburger className={styles.hamburger} open={open} />
        </Popover.Trigger>
        <Portal>
          <Popover.Content>
            <Popover.CloseTrigger
              onClick={closeMenu}
              className={styles.hiddenCloseTrigger}
              aria-label="Close menu"
            />
            <div className={styles.content}>
              {!primarySubNav && (
                <MainNavPanel
                  ancillaryNavigationItems={ancillaryNavigationItems}
                  primaryNavigationItems={primaryNavigationItems}
                  setPrimarySubNav={setPrimarySubNav}
                  closeMenu={closeMenu}
                />
              )}
              {!!primarySubNav && (
                <SubNavPanel
                  closeMenu={closeMenu}
                  nav={primarySubNav.subMenu}
                  setPrimarySubNav={setPrimarySubNav}
                />
              )}
            </div>
          </Popover.Content>
        </Portal>
      </Popover.Root>
    </nav>
  );
};

export default MobilePrimaryNav;
